import { useEffect, useRef, RefObject } from 'react';

type UseScrollStopProps = {
  onStop: () => void;
  delay?: number; // через какое время срабатывает onStop после остановки
  threshold?: number; // сколько процентов элемента должно быть видно, чтобы сработал onStop
  repeatCount?: number; // количество вызовов onStop
};

const DELAY = 1000;
const THRESHOLD = 0.6;
const REPEAT_COUNT = 1;

export const useScrollStopOnView = (
  ref: RefObject<HTMLElement | null>,
  { onStop, delay = DELAY, threshold = THRESHOLD, repeatCount = REPEAT_COUNT }: UseScrollStopProps,
) => {
  const scrollCheckInterval = useRef<NodeJS.Timeout | null>(null);
  const stopTimer = useRef<NodeJS.Timeout | null>(null);
  const lastScrollY = useRef<number>(0);
  const isVisible = useRef(false);
  const stopCallCount = useRef<number>(0);

  const stopMonitoring = () => {
    if (scrollCheckInterval.current) {
      clearInterval(scrollCheckInterval.current);
      scrollCheckInterval.current = null;
    }
    if (stopTimer.current) {
      clearTimeout(stopTimer.current);
      stopTimer.current = null;
    }
  };

  const startMonitoring = () => {
    if (typeof document === 'undefined') {
      return;
    }

    lastScrollY.current = document.documentElement.scrollTop;

    scrollCheckInterval.current = setInterval(() => {
      const currentScroll = document.documentElement.scrollTop;

      if (currentScroll !== lastScrollY.current) {
        lastScrollY.current = currentScroll;
        if (stopTimer.current) {
          clearTimeout(stopTimer.current);
          stopTimer.current = null;
        }
      } else if (!stopTimer.current && isVisible.current) {
        stopTimer.current = setTimeout(() => {
          if (stopCallCount.current < repeatCount) {
            onStop();
            stopCallCount.current += 1;
          }
          if (stopCallCount.current >= repeatCount) {
            stopMonitoring();
          }
        }, delay);
      }
    }, 100);
  };

  useEffect(() => {
    if (typeof document === 'undefined' || !ref.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          isVisible.current = true;
          startMonitoring();
        } else {
          isVisible.current = false;
          stopMonitoring();
        }
      },
      { threshold },
    );

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
      stopMonitoring();
    };
  }, [ref, delay, threshold, repeatCount]);
};
